import React, { useRef, useEffect, useCallback, useState} from 'react';
import Colors from 'components/Colors.js';
import CirklonPlayhead from './CirklonPlayhead.js';
import ResolutionDropdown from "components/toolbar/ResolutionDropdown.js";
import TrackThumbnail from "components/TrackThumbnail.js";
import {
    setSelectedZObject,
    mouseDown
} from 'actions/index.js';
import TrackMidiInputDropdown from "components/settings/TrackMidiInputDropdown.js";
import CheckboxParameter from "components/effects/CheckboxParameter.js";
import {toggleStep} from 'operations/actions.js';
import CirklonAuxEvents from './CirklonAuxEvents.js';
import ChokesAndMatches from 'components/sidebar/ChokesAndMatches.js';
import Bars from './Bars.js';
import TrackFooter from '../v2/TrackFooter.js';
import CirklonStep from './CirklonStep.js';
import {connect} from 'react-redux';
import CirklonParameterChooser from './CirklonParameterChooser.js';
import '../../Cirklon.scss';

const colors = new Colors();

const CirklonEditor = ({
    isFrozen,
    mouseDown,
    stepIds,
    track,
    steps,
    parameter,
    resolution,
    color,
    cursorStep,
    selectedSteps,
    styling,
    setSelectedZObject
}) => { 
    let [selectedBar, setSelectedBar] = useState(0);
    let [isFollowing, setIsFollowing] = useState(true);
    let [stepDatas, setStepDatas] = useState(stepIds);
    let cirklonRef = useRef();
    let stepsRef = useRef();
    let lastStep = useRef(undefined);
    let barLength = resolution.numSteps();
    if (resolution.resolution === 32) {
        barLength /= 2;
    }
    let start = barLength * selectedBar;
    let end = barLength * (selectedBar + 1);

    useEffect(() => {
        setStepDatas(stepIds); 
    }, [stepIds, setStepDatas, ]);

    const legato = useCallback(() => {
        track.sequencerEngine.keyListener.legato();
    }, [track]);
    
    steps = steps ? steps.slice(start, end) : [];
    
    const onDrop = useCallback(() => {
        if (track.sequencerEngine.state.selector.sampleTagDragging) {
            let sequencerEngine = track.sequencerEngine;
            let tags = sequencerEngine.state.selector.sampleTagDragging;
            if (!Array.isArray(tags)) {
                tags = [tags];
            }
            sequencerEngine.searchForSounds(
                tags,
                true,
                track,
                undefined,
                false
            );
            //setSampleTagDragging(undefined);
        }
    }, [track]);

    const onTouchMove = useCallback((e, isStart) => {
        let x=e.touches[0].clientX;
        let y=e.touches[0].clientY;
        let el = document.elementFromPoint(x, y);
        if (el && el.dataset.stepnumber !== undefined) {
            let stepNumber = parseInt(el.dataset.stepnumber);
            if (stepNumber === lastStep.current) {
                return;
            }
            lastStep.current = stepNumber;
            if (isStart) {
                return;
            }
            let className = el.className;
            if (className.includes("step-toggle") || className.includes("cirklon-step")) {
                let stepData = stepDatas[stepNumber];
                track.pattern.clearCache();
                track.sequencerEngine.operationManager.dispatch(
                    toggleStep(track, stepNumber, resolution));
            }
        }
    }, [stepDatas, resolution]);

    if (!steps  || !stepDatas) {
        return '';
    }

    let className = "cirklon-editor";
    if (isFrozen) {
        className += " frozen";
    }

    if (styling && styling["secondary-color"]) {
        if (colors.getBrightness(styling["secondary-color"]) > 120) {
            className += " light-mode";
        }
    }

    if (isFrozen) {
        return (<div
                  style={{backgroundColor: styling && styling["secondary-color"]}}
                  ref={cirklonRef}
                  onMouseDown={e => mouseDown(true)}
                  onMouseUp={e => mouseDown(false)}
                  className={className}>
                  Frozen
                </div>
               );
    }

    return (
        <div
          style={{backgroundColor: styling && styling["secondary-color"]}}
          onDrop={onDrop}
          ref={cirklonRef}
          onMouseDown={e => mouseDown(true)}
          onMouseUp={e => mouseDown(false)}
          className={className}>
          <div
            ref={stepsRef}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            onTouchStart={(e) => onTouchMove(e, true)} onTouchEnd={() => lastStep.current = undefined} onTouchMove={onTouchMove} className="cirklon-steps">
            <CirklonPlayhead track={track}/>
          {steps.map(
              (on, stepNumber) =>
                  <CirklonStep
                    color={color}
                    isCursor={start + stepNumber === cursorStep}
                    resolution={resolution}
                    track={track}
                    i={stepNumber}
                    isSelected={selectedSteps && selectedSteps.includes(stepDatas[start + stepNumber])}
                    stepNumber={start + stepNumber}
                    key={start+'.'+stepNumber} 
                    stepData={stepDatas[start + stepNumber]}
                    parameter={parameter}
                    on={on}/>)}
          </div>
          <CirklonParameterChooser color={color} stepDatas={stepDatas} track={track}/>
          <TrackFooter track={track} isSelectedTrack={true} trackColor={color}>
            <Bars
              stepsRef={stepsRef}
              cirklonRef={cirklonRef}
              track={track}
              isFollowing={isFollowing}
              setIsFollowing={setIsFollowing}
              numberOfSteps={stepDatas.length}
              barLength={barLength}
              resolution={resolution}
              setSelectedBar={setSelectedBar}
              selectedBar={selectedBar}/>
              {track && <ResolutionDropdown sequencerEngine={track.sequencerEngine}/>}
            <div onClick={legato} style={{backgroundColor: color}} className="legato-btn">
              Legato
            </div>
            <TrackThumbnail onClickThumbnail={() => setSelectedZObject(track)} track={track} hasInner={true}/>
          </TrackFooter>
          {isFrozen && <div className="frozen-overlay">
                         <div className="frozen-text">
                           Frozen Track
                         </div>
                       </div>}
        </div>);
}

const mapStateToProps = (state, props) => {
    let resolution = state.sequencer.displayResolution;
    return {
        resolution: state.sequencer.displayResolution,
        stepIds: state.tracks.trackStepIds[props.track.id],
        steps: state.tracks.trackSteps[props.track.id],
        parameter: state.cirklon.cirklonParameterSelected[props.track.id],
        cursorStep: state.mix.selectedStep,
        isFrozen: props.track.isFrozen,
        selectedSteps: state.keyboard.selectedPianoRollSteps
    };
};

const mapDispatchToProps = (dispatch) => {
  return {
      mouseDown: x => dispatch(mouseDown(x)),
      setSelectedZObject: x => dispatch(setSelectedZObject(x))
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(CirklonEditor);
